import React from 'react';
import { NavLink } from 'react-router-dom';
import { useIntl } from '@edx/frontend-platform/i18n';

import { useHeaderFooter, useFacilitatorStatus, useMyCohorts } from '../../hooks';
import type { Cohort } from '../../types';

import messages from './messages';

import '../cohortSubpage.scss';
import './index.scss';

interface MyCohortsProps {
  children?: React.ReactNode;
}

const MyCohorts: React.FC<MyCohortsProps> = ({ children }) => {
  const intl = useIntl();
  const { headerContent, footerContent } = useHeaderFooter(children);
  const { status, loaded: statusLoaded, canCreateCohorts } = useFacilitatorStatus();
  const {
    cohorts, loaded: cohortsLoaded, failed: cohortsFailed, hasMore, isLoadingMore, loadMore,
  } = useMyCohorts();

  // Page access is any facilitator role (co-facilitators carry a FacilitatorRole but
  // cannot create) or any cohort membership. Creation CTAs use `canCreateCohorts`.
  const isFacilitator = status?.is_facilitator === true;
  const hasAccess = isFacilitator || cohorts.length > 0;

  const showRestricted = statusLoaded && cohortsLoaded && !cohortsFailed && !hasAccess;
  const showListError = statusLoaded && cohortsLoaded && cohortsFailed;
  const showListEmpty = statusLoaded && isFacilitator && cohortsLoaded
    && !cohortsFailed && cohorts.length === 0;

  const renderCard = (cohort: Cohort) => (
    <li key={cohort.slug} className="my-cohorts-card">
      <div className="my-cohorts-card-main">
        <div className="my-cohorts-card-name">
          {cohort.name}
          {cohort.my_role === 'co_facilitator' && (
            <span className="my-cohorts-card-badge">
              {intl.formatMessage(messages.coFacilitatorBadge)}
            </span>
          )}
        </div>
        <div className="my-cohorts-card-created">
          {intl.formatMessage(messages.createdOnLabel, {
            date: intl.formatDate(cohort.created, { year: 'numeric', month: 'long', day: 'numeric' }),
          })}
        </div>
      </div>
      <div className="my-cohorts-card-side">
        <span className="my-cohorts-card-member-count">
          {intl.formatMessage(messages.memberCountLabel, { count: cohort.member_count })}
        </span>
        <div className="leg-primary-button">
          <NavLink to={`/cohorts/${cohort.slug}`}>
            {intl.formatMessage(messages.openButton)}
          </NavLink>
        </div>
      </div>
    </li>
  );

  return (
    <>
      <input type="hidden" id="anPageName" name="page" defaultValue="my-cohorts" />
      <div className="container-center-horizontal">
        <div className="my-cohorts screen cohort-subpage">
          <div className="homepage">
            {headerContent}
          </div>
          <div className="legatum-container">
            <div className="c-section">
              <div className="my-cohorts-header">
                <h1 className="leg-title">{intl.formatMessage(messages.pageTitle)}</h1>
                {statusLoaded && canCreateCohorts && (
                  <div className="leg-primary-button">
                    <NavLink to="/cohorts/create">
                      {intl.formatMessage(messages.newCohortButton)}
                    </NavLink>
                  </div>
                )}
              </div>
              {showRestricted && (
                <>
                  <p className="leg-p">{intl.formatMessage(messages.restrictedMessage)}</p>
                  <div className="items-center space-bottom">
                    <div className="leg-primary-button">
                      <NavLink to="/cohorts">{intl.formatMessage(messages.backToCohortsLink)}</NavLink>
                    </div>
                  </div>
                </>
              )}
              {showListError && (
                <p className="form-error form-error-custom">
                  {intl.formatMessage(messages.loadErrorMessage)}
                </p>
              )}
              {/* The header already carries the only create CTA. Co-facilitators
                  (no `canCreateCohorts`) get their own wording. */}
              {showListEmpty && (
                <div className="my-cohorts-empty">
                  <p className="leg-p">
                    {intl.formatMessage(
                      canCreateCohorts ? messages.emptyStateMessage : messages.emptyStateCoFacilitatorMessage,
                    )}
                  </p>
                </div>
              )}
              {statusLoaded && cohorts.length > 0 && (
                <>
                  <ul className="my-cohorts-list">
                    {cohorts.map(renderCard)}
                  </ul>
                  {hasMore && (
                    <div className="my-cohorts-load-more">
                      <button type="button" className="my-cohorts-load-more-button" onClick={loadMore} disabled={isLoadingMore}>
                        {intl.formatMessage(messages.loadMoreButton)}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          {footerContent}
        </div>
      </div>
    </>
  );
};

export default MyCohorts;
